import { applicantSection, otherAttachments, penaltyDeclaration, replySection } from "modules/ebiForms/common";
import { childAddressFields } from "modules/ebiForms/definitions/sharedEducationSafety";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * D8. Wniosek o przyjęcie dziecka do przedszkola albo klasy I szkoły podstawowej (zgłoszenie do szkoły obwodowej) —
 * art. 131, 133 i 149–151 ustawy — Prawo oświatowe; kryteria drugiego etapu i dokumenty określa uchwała rady gminy.
 * Formularz dla gmin BEZ elektronicznego systemu naboru (VULCAN, PCSS NABÓR, Asseco) — wtedy wniosek składa się tam.
 * Brak wzoru ogólnopolskiego; układ jak wniosek Poznania (NABÓR 2026, PCSS, https://nabor.pcss.pl/poznan/przedszkole/File/Download/8297
 * — pobrany 2026-10-22): placówki w kolejności preferencji, dane kandydata (PESEL albo dokument), adres, dane rodziców,
 * oświadczenia, załącznik o spełnianiu kryteriów ustawowych i gminnych. Klasa I spoza obwodu — tylko kryteria gminy
 * (art. 133 ust. 2); szkoła obwodowa — zgłoszenie z oświadczeniem o miejscu zamieszkania (art. 151 ust. 2–3, do
 * potwierdzenia z wydziałem). Oświadczenia o wielodzietności i samotnym wychowywaniu w formularzu z klauzulą karną
 * (art. 150 ust. 6); podpis drugiego rodzica — skan albo wydruk w placówce. Rekrutacja nie jest postępowaniem KPA
 * (wniosek składa rodzic), więc bez sekcji pełnomocnika.
 */
const PRESCHOOL: EbiCondition = { field: "rodzajRekrutacji", equals: "przedszkole" };
const CATCHMENT: EbiCondition = { field: "rodzajRekrutacji", equals: "szkolaObwodowa" };
const RECRUITMENT: EbiCondition = { not: CATCHMENT };
const criterion = (value: string): EbiCondition => ({ all: [PRESCHOOL, { field: "kryteria", includes: value }] });
const LOCAL_CRITERIA: EbiCondition = { all: [RECRUITMENT, { field: "kryteriaGminy", filled: true }] };
const SECOND_PARENT: EbiCondition = { field: "drugiRodzic", equals: "podaje" };

const D8: IEbiFormDefinition = {
  code: "D8",
  version: 1,
  title: "Wniosek o przyjęcie dziecka do przedszkola lub klasy I szkoły podstawowej",
  category: EEbiFormCategory.Education,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Rekrutacja do przedszkola, oddziału przedszkolnego lub klasy I — dla gmin, które nie prowadzą elektronicznego naboru.",
  keywords: ["rekrutacja", "nabór", "przedszkole", "klasa pierwsza", "szkoła podstawowa", "obwód szkoły", "zgłoszenie"],
  organ: "Dyrektor przedszkola lub szkoły (komisja rekrutacyjna) — placówka pierwszego wyboru",
  legalBasis:
    "art. 131, art. 133 i art. 149–151 ustawy z dnia 14 grudnia 2016 r. — Prawo oświatowe; uchwała rady gminy w sprawie kryteriów drugiego etapu postępowania rekrutacyjnego",
  request:
    "Wnoszę o przyjęcie dziecka wskazanego we wniosku do przedszkola (oddziału przedszkolnego) albo klasy I szkoły podstawowej — według wskazanej kolejności preferencji, a w przypadku szkoły obwodowej zgłaszam dziecko do klasy I.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty.",
  variantField: "rodzajRekrutacji",
  variants: {
    przedszkole: { title: "Wniosek o przyjęcie dziecka do przedszkola" },
    szkolaObwodowa: { title: "Zgłoszenie dziecka do klasy I szkoły podstawowej obwodowej", documentType: "Pismo" },
    szkolaSpozaObwodu: { title: "Wniosek o przyjęcie dziecka do klasy I szkoły podstawowej spoza obwodu" },
  },
  sections: [
    {
      code: "rekrutacja",
      title: "Rekrutacja",
      fields: [
        {
          code: "infoSystemNaboru",
          type: "info",
          label:
            "Formularz jest przeznaczony dla gmin, które nie prowadzą elektronicznego systemu naboru. Jeśli Twoja gmina korzysta z takiego systemu (np. VULCAN, PCSS NABÓR, Asseco), złóż wniosek w tym systemie — informację podaje komunikat gminy o rekrutacji. Wniosek składa się w terminach z harmonogramu rekrutacji.",
        },
        {
          code: "rodzajRekrutacji",
          type: "radio",
          label: "Wniosek dotyczy",
          required: true,
          options: [
            { value: "przedszkole", label: "przyjęcia do przedszkola, oddziału przedszkolnego w szkole podstawowej lub innej formy wychowania przedszkolnego" },
            { value: "szkolaObwodowa", label: "zgłoszenia do klasy I szkoły podstawowej, w której obwodzie dziecko mieszka" },
            { value: "szkolaSpozaObwodu", label: "przyjęcia do klasy I szkoły podstawowej spoza obwodu (rekrutacja na wolne miejsca)" },
          ],
        },
        {
          code: "preferencje",
          type: "repeat",
          label: "Placówki w kolejności od najbardziej do najmniej preferowanej",
          hint: "Pierwsza pozycja to placówka pierwszego wyboru. Liczbę placówek może ograniczać regulamin rekrutacji gminy.",
          itemLabel: "Preferencja",
          required: true,
          visibleWhen: RECRUITMENT,
          min: 1,
          max: 5,
          fields: [
            { code: "nazwa", type: "text", label: "Nazwa przedszkola lub szkoły", required: true, width: "half", maxLength: 300 },
            { code: "adres", type: "text", label: "Adres", required: true, width: "half", maxLength: 300 },
          ],
        },
        { code: "szkolaObwodowaNazwa", type: "text", label: "Nazwa i adres szkoły obwodowej", required: true, visibleWhen: CATCHMENT, maxLength: 300 },
      ],
    },
    applicantSection("Rodzic (opiekun prawny) składający wniosek"),
    {
      code: "kandydat",
      title: "Dane kandydata",
      fields: [
        { code: "dzieckoImie", type: "text", label: "Imię", required: true, width: "third", maxLength: 100 },
        { code: "dzieckoDrugieImie", type: "text", label: "Drugie imię (jeżeli posiada)", width: "third", maxLength: 100 },
        { code: "dzieckoNazwisko", type: "text", label: "Nazwisko", required: true, width: "third", maxLength: 100 },
        { code: "dzieckoDataUrodzenia", type: "date", label: "Data urodzenia", required: true, width: "half" },
        {
          code: "dzieckoPesel",
          type: "pesel",
          label: "PESEL",
          requiredWhen: { not: { field: "dzieckoDokument", filled: true } },
          width: "half",
        },
        {
          code: "dzieckoDokument",
          type: "text",
          label: "Seria i numer paszportu lub innego dokumentu tożsamości (gdy dziecko nie ma numeru PESEL)",
          requiredWhen: { not: { field: "dzieckoPesel", filled: true } },
          maxLength: 50,
        },
        ...childAddressFields("dziecko"),
      ],
    },
    {
      code: "rodzice",
      title: "Dane rodziców (opiekunów prawnych)",
      description: "Rodzic to także opiekun prawny dziecka oraz osoba (podmiot) sprawująca pieczę zastępczą nad dzieckiem.",
      fields: [
        {
          code: "rodzicRola",
          type: "radio",
          label: "Wnioskodawca jest",
          required: true,
          options: [
            { value: "matka", label: "matką dziecka" },
            { value: "ojciec", label: "ojcem dziecka" },
            { value: "opiekun", label: "opiekunem prawnym albo osobą sprawującą pieczę zastępczą" },
          ],
        },
        {
          code: "drugiRodzic",
          type: "radio",
          label: "Drugi rodzic (opiekun prawny)",
          required: true,
          defaultValue: "podaje",
          options: [
            { value: "podaje", label: "podaję dane drugiego rodzica (opiekuna prawnego)" },
            { value: "nieDotyczy", label: "nie dotyczy — dziecko ma jednego rodzica (opiekuna prawnego)" },
          ],
        },
        { code: "drugiRodzicImie", type: "text", label: "Imię", required: true, visibleWhen: SECOND_PARENT, width: "half", maxLength: 100 },
        { code: "drugiRodzicNazwisko", type: "text", label: "Nazwisko", required: true, visibleWhen: SECOND_PARENT, width: "half", maxLength: 100 },
        {
          code: "drugiRodzicAdres",
          type: "text",
          label: "Adres zamieszkania (jeśli inny niż adres dziecka)",
          visibleWhen: SECOND_PARENT,
          maxLength: 300,
        },
        { code: "drugiRodzicTelefon", type: "phone", label: "Telefon (jeżeli posiada)", visibleWhen: SECOND_PARENT, width: "half" },
        { code: "drugiRodzicEmail", type: "email", label: "Adres e-mail (jeżeli posiada)", visibleWhen: SECOND_PARENT, width: "half" },
        {
          code: "podpisDrugiegoRodzica",
          type: "radio",
          label: "Podpis drugiego rodzica pod wnioskiem",
          hint: "Wiele placówek wymaga podpisów obojga rodziców.",
          required: true,
          visibleWhen: SECOND_PARENT,
          options: [
            { value: "skan", label: "dołączam skan wniosku albo oświadczenia podpisanego przez drugiego rodzica" },
            { value: "wPlacowce", label: "drugi rodzic podpisze wniosek w placówce, jeśli będzie to wymagane" },
          ],
        },
      ],
    },
    {
      code: "kryteriaUstawowe",
      title: "Kryteria ustawowe",
      description:
        "Zaznacz kryteria, które spełnia kandydat, i dołącz dokumenty, o których mowa w art. 150 ust. 2 ustawy — Prawo oświatowe. Niezaznaczone kryterium nie jest brane pod uwagę.",
      visibleWhen: PRESCHOOL,
      fields: [
        {
          code: "kryteria",
          type: "checkboxes",
          label: "Kandydat spełnia kryteria",
          options: [
            { value: "wielodzietnosc", label: "wielodzietność rodziny kandydata (troje i więcej dzieci)" },
            { value: "niepelnosprawnoscKandydata", label: "niepełnosprawność kandydata" },
            { value: "niepelnosprawnoscRodzica", label: "niepełnosprawność jednego z rodziców kandydata" },
            { value: "niepelnosprawnoscRodzicow", label: "niepełnosprawność obojga rodziców kandydata" },
            { value: "niepelnosprawnoscRodzenstwa", label: "niepełnosprawność rodzeństwa kandydata" },
            { value: "samotneWychowywanie", label: "samotne wychowywanie kandydata w rodzinie" },
            { value: "piecza", label: "objęcie kandydata pieczą zastępczą" },
          ],
        },
      ],
    },
    {
      code: "kryteriaGminne",
      title: "Kryteria określone przez gminę",
      description: "Kryteria i dokumenty potwierdzające ich spełnianie określa uchwała rady gminy — znajdziesz je w komunikacie o rekrutacji.",
      visibleWhen: RECRUITMENT,
      fields: [
        {
          code: "kryteriaGminy",
          type: "textarea",
          label: "Kryteria określone uchwałą rady gminy, które spełnia kandydat",
          placeholder: "np. oboje rodzice pracują; rodzeństwo kandydata uczęszcza do tej samej placówki",
          maxLength: 2000,
        },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        {
          code: "oswiadczenieWielodzietnosc",
          type: "declaration",
          label: "Oświadczam, że kandydat wychowuje się w rodzinie wielodzietnej, czyli wychowującej troje i więcej dzieci.",
          required: true,
          visibleWhen: criterion("wielodzietnosc"),
        },
        {
          code: "oswiadczenieSamotne",
          type: "declaration",
          label: "Oświadczam, że samotnie wychowuję kandydata oraz nie wychowuję żadnego dziecka wspólnie z jego rodzicem.",
          required: true,
          visibleWhen: criterion("samotneWychowywanie"),
        },
        {
          code: "oswiadczenieZamieszkanie",
          type: "declaration",
          label: "Oświadczam, że kandydat i jego rodzice zamieszkują pod adresami podanymi w zgłoszeniu, w obwodzie wskazanej szkoły.",
          required: true,
          visibleWhen: CATCHMENT,
        },
        {
          code: "oswiadczenieDokumenty",
          type: "declaration",
          label: "Oświadczam, że zgodnie z art. 150 ust. 2 ustawy — Prawo oświatowe do wniosku dołączam dokumenty potwierdzające spełnianie wskazanych kryteriów.",
          required: true,
          visibleWhen: { any: [{ all: [PRESCHOOL, { field: "kryteria", filled: true }] }, LOCAL_CRITERIA] },
        },
        {
          code: "oswiadczenieDane",
          type: "declaration",
          label: "Oświadczam, że dane przedłożone w niniejszym wniosku są zgodne ze stanem faktycznym.",
          required: true,
        },
        {
          ...penaltyDeclaration(),
          visibleWhen: { any: [CATCHMENT, criterion("wielodzietnosc"), criterion("samotneWychowywanie"), LOCAL_CRITERIA] },
        },
      ],
    },
    replySection("Sposób odbioru informacji w sprawie"),
  ],
  attachments: [
    {
      code: "orzeczenieKandydata",
      label: "Orzeczenie o potrzebie kształcenia specjalnego wydane ze względu na niepełnosprawność albo orzeczenie o niepełnosprawności lub o stopniu niepełnosprawności kandydata",
      hint: "Lub orzeczenie równoważne. Kopia poświadczona za zgodność z oryginałem przez rodzica (art. 150 ust. 3 i 5).",
      visibleWhen: criterion("niepelnosprawnoscKandydata"),
      required: true,
    },
    {
      code: "orzeczenieRodzica",
      label: "Orzeczenie o niepełnosprawności lub o stopniu niepełnosprawności jednego z rodziców kandydata",
      hint: "Lub orzeczenie równoważne.",
      visibleWhen: criterion("niepelnosprawnoscRodzica"),
      required: true,
    },
    {
      code: "orzeczeniaRodzicow",
      label: "Orzeczenia o niepełnosprawności lub o stopniu niepełnosprawności obojga rodziców kandydata",
      hint: "Lub orzeczenia równoważne.",
      visibleWhen: criterion("niepelnosprawnoscRodzicow"),
      required: true,
      multiple: true,
    },
    {
      code: "orzeczenieRodzenstwa",
      label: "Orzeczenie o niepełnosprawności lub o stopniu niepełnosprawności rodzeństwa kandydata",
      hint: "Lub orzeczenie równoważne.",
      visibleWhen: criterion("niepelnosprawnoscRodzenstwa"),
      required: true,
      multiple: true,
    },
    {
      code: "samotneWychowywanieDokument",
      label: "Prawomocny wyrok sądu rodzinnego orzekający rozwód lub separację albo akt zgonu",
      hint: "Gdy samotne wychowywanie wynika z rozwodu, separacji albo śmierci rodzica.",
      visibleWhen: criterion("samotneWychowywanie"),
    },
    {
      code: "pieczaDokument",
      label: "Dokument poświadczający objęcie dziecka pieczą zastępczą",
      visibleWhen: criterion("piecza"),
      required: true,
    },
    {
      code: "kryteriaGminyDokumenty",
      label: "Dokumenty potwierdzające spełnianie kryteriów określonych przez gminę",
      hint: "Np. zaświadczenie pracodawcy, wydruk z CEIDG, zaświadczenie o nauce, pierwsza strona zeznania PIT, oświadczenie o dochodzie — według uchwały rady gminy.",
      visibleWhen: LOCAL_CRITERIA,
      required: true,
      multiple: true,
    },
    {
      code: "podpisDrugiegoRodzicaPlik",
      label: "Wniosek albo oświadczenie podpisane przez drugiego rodzica (opiekuna prawnego)",
      visibleWhen: { all: [SECOND_PARENT, { field: "podpisDrugiegoRodzica", equals: "skan" }] },
      required: true,
    },
    otherAttachments(),
  ],
};

export default D8;
